import axios from 'axios';
import { S3_IMAGE_BUCKET_URL } from '../constants';

const api = axios.create();

let DISABLE_CACHE = false;
const DATA_CACHE = {};

// TODO: this is a temporary hack while the backend doesn't return year
// descriptions. It should be removed.
export const decipherYear = (year, y2k = true) => {
  // Basic pattern: `8015` means 1980–2015. `00015` means 2000-2015.

  // If the year starts with 19 or 20, assume it doesn't need to be decoded
  if (/^(19|20)/.test(year) || !y2k) return year;

  // Assume 4 digits, representing decade/year 1 & decade/year 2
  const [d1, y1, d2, y2] = year;
  // if first digit is > 3, assume century is 1900's. Always assume second
  // part of range is in the 21st century.
  return `${d1 >= 3 ? '19' : '20'}${d1}${y1}-20${d2}${y2}`;
};

const fetchIndicator = (id, displayRev) =>
  api
    .get(
      `/display-groups/${id}?${displayRev ? `displayRev=${displayRev}` : ''}`,
    )
    .then((response) => response.data);

const getImagePath = (path) => {
  const requestPath = `${S3_IMAGE_BUCKET_URL}&url=${encodeURIComponent(path)}`;
  return requestPath;
};

const fetchBatchedData = (params, info, offset = 0, results = []) => {
  const limit = params.limit || 20;
  return api
    .get('/query', {
      params: {
        ...params,
        // For maps we don't need to expand filters, and disabling it speeds
        // up the query by quite a lot
        expand: info.isMap ? undefined : ['filter', 'geo'],
        limit,
        offset,
        paginate: undefined,
      },
    })
    .then((response) => {
      const all = [].concat(results, response.data.content);
      if (!params.paginate || response.data.metadata.totalCount < limit) {
        return all;
      }
      return api.fetchBatchedData(params, info, offset + 1, all);
    })
    .catch(() => []);
};

const fetchPowerpoint = (params) => {
  const theme = params.theme ?? '';
  const screenshotUrl = params.screenshotUrl
    ? `${encodeURIComponent(params.screenshotUrl)}`
    : '';
  const tokenContext = params.tokenContext ?? {};
  const caption = params.caption ? `${encodeURIComponent(params.caption)}` : '';

  const cacheParams = {
    type: 'powerpoint',
    indicatorId: params.indicator,
    displayId: params.display,
    theme: params.theme,
    breakdownId: params.breakdown,
  };

  const requestKey = JSON.stringify(cacheParams);

  if (DATA_CACHE[requestKey]) return DATA_CACHE[requestKey];

  const out = api.post(
    `/display-groups/${params.indicator}/${params.display}/ppt`,
    params,
    {
      responseType: 'blob',
    },
  );

  if (!DISABLE_CACHE) DATA_CACHE[requestKey] = out;

  return out;
};

const fetchExcel = (params) => {
  const cacheParams = {
    type: 'excel',
    ...params,
  };

  const requestKey = JSON.stringify(cacheParams);

  if (DATA_CACHE[requestKey]) return DATA_CACHE[requestKey];

  const out = api.post(
    `/display-groups/${params.indicator}/${params.display}/xlsx`,
    params,
    {
      responseType: 'blob',
    },
  );

  if (!DISABLE_CACHE) DATA_CACHE[requestKey] = out;

  return out;
};

const fetchGeos = (params, additionalGeoCodes, info) => {
  const requestKey = JSON.stringify({
    fetch: 'geos',
    params,
    additionalGeoCodes,
    info,
  });
  const out = api
    .get('/geography', { params: { geo_code: additionalGeoCodes } })
    .then((reses) =>
      axios.all(
        reses.data.content.map((geo) =>
          api.get(`/geography/geometry/${geo.geo_code}`).then((res) => ({
            ...geo,
            geometry: res.data.content,
          })),
        ),
      ),
    );

  if (!DISABLE_CACHE) DATA_CACHE[requestKey] = out;

  return out;
};

const fetchDataRevisions = async (dataset) => {
  try {
    const { data } = await api.get(`/datasets/${dataset}/revisions`);
    return data;
  } catch (err) {
    return { results: [] };
  }
};

const fetchDisplayRevisions = async (slug) => {
  try {
    const { data } = await api.get(`/display-groups/${slug}/revisions`);
    return data;
  } catch (err) {
    return { results: [] };
  }
};

const fetchData = async (params, additionalGeoCodes) => {
  let geoCodes = [];
  if (params.geo_code) {
    geoCodes = geoCodes.concat(
      Array.isArray(params.geo_code) ? params.geo_code : [params.geo_code],
    );
  }
  if (additionalGeoCodes) {
    geoCodes = geoCodes.concat(additionalGeoCodes);
  }

  const filters = {
    ...(geoCodes.length ? { geo_code_long: geoCodes } : null),
    ...params.args,
    ...(params.geo_type && params.geo_type.length
      ? { geo_type: params.geo_type }
      : null),
  };

  const { data } = await api.post(
    `/display-groups/${params.indicator_id}/${params.display_id}/data`,
    {
      filters,
      format: params.format,
      fields: params.fields,
      datasetRev: params.datasetName,
      displayRev: params.displayRev,
    },
  );
  ((data || {}).results || []).forEach((datum, i) => {
    // eslint-disable-next-line no-param-reassign
    datum.UNIQUE_ID = i;
    // eslint-disable-next-line no-param-reassign
    datum.unique_id_long = i;
    data.attributes.forEach((attr) => {
      if (datum[attr.field] === attr.key)
        // eslint-disable-next-line no-param-reassign
        datum[`${attr.field}_${attr.type}`] = attr.value;
    });
  });
  return data;
};

const setTenant = (tenantKey) => {
  api.defaults.headers.common['x-tenant'] = tenantKey || 'nea';
};

const setBaseURL = (baseURL) => {
  api.defaults.baseURL = baseURL || '/api';
};

export const updateDefaults = (options) => {
  // Set Axios defaults
  setBaseURL(options.proxy || options.target);
  api.defaults.headers.common.mode = 'cors';
  DISABLE_CACHE = Boolean(options.disableCaching);
  // TODO: switch tenant according to domain/cookie?
  if (options.tenant) setTenant(options.tenant);
  if (options.baseURL) setBaseURL(options.baseURL);
};

const buildUrlToDownloadDisplay = (params) => {
  const baseUrl = api.defaults.baseURL;
  const tenant = `&tenant=${api.defaults.headers.common['x-tenant']}`;

  const displayParams = `/display-groups/${params.indicator}/${params.display}/data?`;
  const fileFormat = `&extension=${params.format}`;

  const formattedGeo = () => {
    let geo = '';
    if (params.geo.geoType) {
      geo += `&filters=geo_type:${params.geo.geoType}`;
    }
    if (params.geo.geoCodeLong) {
      geo += `&filters=geo_code_long:${params.geo.geoCodeLong}`;
    }
    if (params.geo.primaryGeo) {
      geo += `&filters=geo_code_long:${params.geo.primaryGeo}`;
    }
    if (params.geo.secondaryGeo) {
      geo += `&filters=geo_code_long:${params.geo.secondaryGeo}`;
    }
    return geo;
  };

  const fields = params.fields?.map((item) => `&fields=${item}`).join('') ?? '';

  const filters =
    params.filters
      ?.map((item) => {
        return `&filters=${item.join(':')}`;
      })
      .join('') ?? '';

  const theme = params.theme ? `&theme=${params.theme}` : '';

  const screenshotUrl = params.screenshotUrl
    ? `&screenshotUrl=${encodeURIComponent(params.screenshotUrl)}`
    : '';

  const tokenContext = params.tokenContext
    ? Object.keys(params.tokenContext)
        .map((key) => {
          return params.tokenContext[key]
            ? `&tokenContext=${key}:${params.tokenContext[key]}`
            : '';
        })
        .join('')
    : '';

  return `${baseUrl}${displayParams}${tenant}${fileFormat}${formattedGeo()}${fields}${filters}${theme}${screenshotUrl}${tokenContext}`;
};

export default {
  get: api.get,

  fetchIndicator,
  getImagePath,
  fetchBatchedData,
  fetchPowerpoint,
  fetchGeos,
  fetchDataRevisions,
  fetchDisplayRevisions,
  fetchData,
  fetchExcel,
  setTenant,
  setBaseURL,

  buildUrlToDownloadDisplay,
};
